/**
 * MobileDrawer - Slide-in left drawer for secondary nav on mobile.
 * Triggered by avatar in mobile AppHeader. Items: Profile, Clubs, Events, Store, Log out.
 * Backdrop closes on tap.
 *
 * Drives its enter/exit animation via `useSlideTransition` so the panel slides
 * in from the left when opened and slides back out before unmount. The avatar
 * is sized to match the header avatar (w-9 h-9) so it appears to slide into
 * place rather than visually grow as the drawer covers the header.
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser, logout } from '../../store/auth.reducer';
import useSlideTransition from '../../hooks/useSlideTransition';
import useOverlayBackClose from '../../hooks/useOverlayBackClose';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import { cleanupDeviceTokenOnLogout } from '../../helpers/deviceTokenHelpers';
import { clearAppIconBadge } from '../../helpers/appBadgeHelpers';
import NavIcon from './NavIcon';
import UserAvatar from '../Profile/UserAvatar';

const SECONDARY_ITEMS = [
  { to: null, label: 'Profile', icon: 'profile' },
  { to: '/clubs', label: 'Clubs', icon: 'clubs' },
  { to: '/events', label: 'Events', icon: 'events' },
  { to: '/store', label: 'Store', icon: 'store' },
];

function MobileDrawer({ open, onClose }) {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mounted, shown } = useSlideTransition(open);
  useOverlayBackClose(open, onClose);
  const { ref: panelRef, dragX } = useSwipeToClose({
    open,
    direction: 'left',
    onClose,
  });
  const displayName = user?.displayname || user?.username || 'Profile';

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleLogout = async () => {
    await cleanupDeviceTokenOnLogout();
    await clearAppIconBadge();
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // network failure shouldn't block logout
    }
    dispatch(logout());
    onClose?.();
    navigate('/');
  };

  if (!mounted) return null;

  const dragging = dragX !== 0;
  const panelStyle = dragging ? { transform: `translateX(${dragX}px)` } : undefined;
  const transitionClass = dragging ? 'transition-none' : 'transition-transform duration-300 ease-zen';

  return (
    <div className="md:hidden fixed inset-0 z-30" data-testid="mobile-drawer">
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className={`absolute inset-0 bg-void/70 backdrop-blur-sm transition-opacity duration-300 ease-zen ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
        data-testid="mobile-drawer-backdrop"
      />
      <aside
        ref={panelRef}
        style={panelStyle}
        className={`relative h-full w-72 max-w-[80%] bg-charcoal border-r border-shadow flex flex-col pt-safe-top pb-safe-bottom ${transitionClass} ${
          shown ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="px-4 py-3 border-b border-shadow flex items-center gap-3">
          <UserAvatar user={user} className="w-9 h-9 rounded-full flex-shrink-0" emojiClassName="text-base" />
          <div className="min-w-0">
            <p className="text-ether font-display font-bold truncate">{displayName}</p>
            {user?.username && (
              <p className="text-silver text-xs truncate">@{user.username}</p>
            )}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 flex flex-col gap-1">
          {SECONDARY_ITEMS.map((item) => {
            const to = item.to ?? '/profile';
            return (
              <Link
                key={item.label}
                to={to}
                onClick={onClose}
                className="flex items-center gap-3 w-full py-3 px-3 rounded-lg text-ash text-sm hover:bg-shadow hover:text-ether transition-colors"
              >
                <NavIcon icon={item.icon} className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
          <div className="flex-1" />
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 w-full py-3 px-3 rounded-lg text-ash text-sm hover:bg-shadow hover:text-energy transition-colors text-left mt-2"
            data-testid="mobile-drawer-logout"
          >
            <NavIcon icon="logout" className="w-5 h-5" />
            <span>Log out</span>
          </button>
        </nav>
      </aside>
    </div>
  );
}

export default MobileDrawer;
